import styles from './Account.module.scss';
import * as React from 'react';

import {
  IColumn,
  MessageBarType,
  MessageBar,
  Breadcrumb,
  Pivot,
  PivotItem,
  DetailsList,
  CheckboxVisibility,
  IContextualMenuItem,
  Persona,
  PersonaSize,
  TextField,
  Panel,
  PanelType,
  Link,
  Checkbox,
  Spinner,
  Label,
  Stack,
  SpinnerSize,
} from '@fluentui/react';

import { ToastParams } from '../../redux/message/types';
import { AccountResponse } from '../../api/accounts.types';
import { AsyncData } from '../../redux/core';
import { ROUTES } from '../../config/routes';
import { AccountForm } from '../create-visit/components/AccountForm';
import { HealAPI } from '../../healApi';
import { Patient } from '../../api';
import { PageHeader } from '../core/PageHeader';
import { ManagingAgencies } from './ManagingAgencies';
import { resetPassword } from '../../api/accounts';
import { Clipboard } from '../core/Clipboard';
import { Tile } from '../core/Tile';
import { NavigateFunction } from 'react-router-dom';

export interface AccountOwnProps {
  params: { id: string; tab: string };
  navigate: NavigateFunction;
}

export interface AccountStateProps {
  isUpdating: boolean;
}

export interface AccountDispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    editUserAccount: (userAccountId: string, data: any) => void;
    toast: (params: ToastParams) => void;
    clearError: () => void;
  };
}

export type AccountProps = AccountOwnProps & AccountStateProps & AccountDispatchProps;

export interface AccountState {
  model: AsyncData<AccountResponse>;
  addressColumns: IColumn[];
  patientColumns: IColumn[];
  showEditAccountPanel: boolean;
}

export class Account extends React.Component<AccountProps, AccountState> {
  constructor(props: AccountProps) {
    super(props);
    this.state = {
      patientColumns: this.buildPatientColumns(),
      addressColumns: this.buildAddressColumns(),
      model: { isFetching: false },
      showEditAccountPanel: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Account');
    this.fetchAccount(this.props.params.id);
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Account');
  }

  public componentDidUpdate(prevProps: AccountProps) {
    if (prevProps.isUpdating && !this.props.isUpdating) {
      this.setState({ showEditAccountPanel: false });
      this.fetchAccount(this.props.params.id);
    }
  }

  public render(): JSX.Element {
    if (this.state.model.error) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.model.error}</MessageBar>
        </div>
      );
    }

    const breadcrumb = (
      <Breadcrumb
        items={[
          { text: 'Users', key: 'users', onClick: this.onUsersClick },
          {
            text: this.state.model.data?.fullName || 'Loading...',
            key: 'account',
            isCurrentItem: true,
          },
        ]}
        // Returning undefined to OnReduceData tells the breadcrumb not to shrink
        onReduceData={() => undefined}
      />
    );

    return (
      <div className={styles.accountPage}>
        <PageHeader
          documentTitle={this.state.model.data?.lastName || 'Loading...'}
          title={breadcrumb}
          isLoading={this.state.model.isFetching}
          showShareButton={true}
          actions={{ items: this.getActions(), type: 'dropdown' }}
        />
        {this.state.model.data?.status === 'STATUS_DISABLED' && <MessageBar messageBarType={MessageBarType.severeWarning}>This user account is disabled.</MessageBar>}
        <Pivot onLinkClick={this.onPivotLinkClick} selectedKey={this.props.params.tab || 'profile'}>
          <PivotItem headerText="Profile" itemKey="profile">
            {this.renderAccount()}
          </PivotItem>
          <PivotItem headerText="Patients" itemKey="patients">
            <DetailsList
              checkboxVisibility={CheckboxVisibility.hidden}
              items={(this.state.model.data && this.state.model.data.patients) || []}
              setKey="set"
              columns={this.state.patientColumns}
            />
          </PivotItem>
          <PivotItem headerText="Addresses" itemKey="addresses">
            <DetailsList
              checkboxVisibility={CheckboxVisibility.hidden}
              items={(this.state.model.data && this.state.model.data.addresses) || []}
              setKey="set"
              columns={this.state.addressColumns}
            />
          </PivotItem>
        </Pivot>
        {this.renderAccountSidePanel()}
      </div>
    );
  }

  private onPivotLinkClick = (item?: PivotItem) => {
    this.props.navigate(`${ROUTES.accounts}/${this.props.params.id}/${item!.props.itemKey!}`);
  };

  private getActions(): IContextualMenuItem[] {
    if (this.state.model.isFetching || !this.state.model.data) {
      return [];
    }

    return [
      {
        key: 'Edit Account',
        name: 'Edit Account',
        iconProps: { iconName: 'Edit' },
        onClick: this.onEditAccount,
      },
      {
        key: 'Reset email',
        name: 'Reset password email',
        iconProps: { iconName: 'MailAlert' },
        onClick: () => {
          resetPassword(this.state.model.data!.email).then(() => {
            this.props.actions.toast({
              type: 'success',
              title: 'Success',
              content: `Forgot password email has been sent to ${this.state.model.data!.email}.`,
            });
          });
        },
      },
    ];
  }

  private renderAccount(): JSX.Element | null {
    if (this.state.model.isFetching) {
      return <Spinner style={{ marginTop: 20 }} size={SpinnerSize.large} label="Fetching Account information..." />;
    }

    const account = this.state.model.data;
    if (!account) {
      return null;
    }

    return (
      <Stack root={{ style: { marginTop: 10 } }} tokens={{ childrenGap: 12 }}>
        <Stack horizontal>
          <Persona
            size={PersonaSize.size100}
            imageUrl={account.avatarUrl}
            text={account.firstName + ' ' + account.lastName}
            secondaryText={account.email}
            tertiaryText={account.phoneNumber}
          />
          <Tile text="Patients" value={account.patients.length} />
          <Tile text="Visits" value={account.totalCompletedVisits} />
        </Stack>

        <Stack horizontal>
          <TextField label="Zip code" value={account.signupZipcode || account.zipcode || 'N/A'} borderless={true} readOnly={true} />
          <TextField label="Person type" value={account.userAccountType || 'N/A'} borderless={true} readOnly={true} />
          <Clipboard content={account.userAccountId} message="Account ID has been copied to clipboard">
            <TextField label="Account ID" value={account.userAccountId} styles={{ field: { width: 310, ':hover': { cursor: 'copy' } } }} borderless readOnly />
          </Clipboard>
        </Stack>

        <Stack>
          <Label>Managed by Partner(s)</Label>
          <ManagingAgencies userAccountId={account.userAccountId} />
        </Stack>
      </Stack>
    );
  }

  private onEditAccount = () => {
    this.setState({ showEditAccountPanel: true });
  };

  private renderAccountSidePanel(): JSX.Element | null {
    const account = this.state.model.data;
    if (!account) {
      return null;
    }

    return (
      <Panel
        isBlocking={false}
        isOpen={this.state.showEditAccountPanel}
        onDismiss={() => this.setState({ showEditAccountPanel: false })}
        type={PanelType.medium}
        headerText={'Edit account'}
      >
        <AccountForm account={this.state.model.data} isUpdating={this.props.isUpdating} actions={this.props.actions} />
      </Panel>
    );
  }

  private onUsersClick = () => {
    this.props.navigate(ROUTES.patients);
  };

  private fetchAccount(accountId: string) {
    this.setState({ model: { isFetching: true } });
    HealAPI.fetchAccountInfo(accountId).then((response) => {
      if (response && response.data) {
        const accountModel: AccountResponse = response.data;
        this.setState({ model: { data: accountModel, isFetching: false } });
      } else {
        this.setState({ model: { isFetching: false, error: 'The account does not exist.' } });
      }
    });
  }

  private buildPatientColumns(): IColumn[] {
    return [
      {
        key: 'patient',
        name: 'Patient',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: Patient) => {
          const onRenderPrimaryText = () => {
            return <Link onClick={() => this.props.navigate(`${ROUTES.patients}/${item.patientId}`)}>{item.fullName}</Link>;
          };
          return <Persona size={PersonaSize.size24} text={item.fullName} onRenderPrimaryText={onRenderPrimaryText} imageUrl={item.avatarUrl} />;
        },
      },
      {
        key: 'email',
        name: 'Email',
        fieldName: 'email',
        minWidth: 120,
        maxWidth: 240,
      },
      {
        key: 'phone',
        name: 'Phone',
        fieldName: 'phone',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        key: 'chartId',
        name: 'Chart ID',
        fieldName: 'chartId',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        key: 'mainPatientOfAccount',
        name: 'Main Patient',
        fieldName: 'mainPatientOfAccount',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: Patient) => {
          return <Checkbox disabled={true} checked={item.mainPatientOfAccount} />;
        },
      },
    ];
  }

  private buildAddressColumns(): IColumn[] {
    return [
      {
        key: 'address',
        name: 'Address',
        minWidth: 80,
        maxWidth: 400,
        fieldName: 'address',
      },
      {
        key: 'addressType',
        name: 'Address Type',
        minWidth: 100,
        maxWidth: 100,
        fieldName: 'addressType',
      },
      {
        key: 'zipcode',
        name: 'Zipcode',
        minWidth: 80,
        maxWidth: 80,
        fieldName: 'zipcode',
      },
      {
        key: 'instructions',
        name: 'Entry Instructions',
        minWidth: 320,
        maxWidth: 320,
        fieldName: 'instructions',
        isMultiline: true,
      },
    ];
  }
}
